import { Button } from '@almbrand/button';
import { Grid, GridCell } from '@almbrand/grid';
import { Headline } from '@almbrand/headline';
import { Modal } from '@almbrand/modal';
import { MultiColItem } from '@almbrand/multicolitem';
import { Paragraph } from '@almbrand/paragraph';
import { RichText } from '@almbrand/richtext';
import { SearchField } from '@almbrand/searchfield';
import { SystemIcons } from '@almbrand/systemicons';
import { pushToDataLayer } from '@almbrand/trackinghooks';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { formatDate } from 'common/DocumentService';
import { DownloadTracking, InternalSearchTracking } from 'common/trackingUtils';
import { ExportCsvButton } from 'components/1-atoms/ExportCsvButton/ExportCsvButton';
import { LoadingSpinner } from 'components/1-atoms/LoadingSpinner';
import { PolicyPreviewButton } from 'components/1-atoms/PolicyPreviewButton';
import { PreviewModal } from 'components/2-molecules/PreviewModal/PreviewModal';
import { HttpStatusCode } from 'constantsValues';
import { useDesignSystemThemeName } from 'hooks/useDesignSystemThemeName';
import usePolicyPreview from 'hooks/usePolicyPreview';
import { useThemeName } from 'hooks/useThemeName';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchPolicyObjectsExport } from 'store/slices/policyObjectsExportSlice';
import { fetchPolicyOverviewDocument } from 'store/slices/policyOverviewDocumentSlice';
import { dataUrlToBlob, downloadBlobFile } from 'utilities/downloadUtils';
import { getStatusMessage } from 'utilities/statusMessageUtils';
import styles from './PolicyListComponent.module.scss';

// Define the shape of a policy object
interface Policy {
	title: string;
	subtitle?: string;
	type?: string;
	policy_number?: string;
	price?: {
		amount: number;
		unit: string;
	};
	group?: {
		title: string;
		type: string;
	};
	source_system?: string;
	end?: {
		title: string;
		date: string;
	};
}

export interface PolicyListComponentProps {
	mainArea: {
		items: Array<{
			content: {
				title: string;
				icon: {
					url: string;
					properties: {
						altText?: string;
					};
				};
			};
		}>;
	};

	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};
	title: string;
}

const PolicyListComponent: React.FC<PolicyListComponentProps> = ({ mainArea, title, statusMessages }) => {
	const policies = useAppSelector((state) => state.policies.policies) as Policy[] | undefined;
	const total = useAppSelector((state) => state.policies.total);
	const status = useAppSelector((state) => state.policies.status);
	const dispatch = useAppDispatch();
	const { isMobile, isTablet } = useDeviceQuery();
	const designSystemThemeName = useDesignSystemThemeName();
	const trackingThemeName = useThemeName();

	const {
		openModal,
		previewDocument,
		handlePolicyClick,
		handlePolicyDownload,
		openBlobPreview,
		onModalClose,
		resetError,
		error,
	} = usePolicyPreview();

	const [searchTerm, setSearchTerm] = useState<string>(''); // State for search input

	// SearchTracking
	useEffect(() => {
		if (searchTerm === '') return; // Don't track empty searches

		const timeoutId = setTimeout(() => {
			pushToDataLayer(InternalSearchTracking(searchTerm, 'search results', trackingThemeName));
		}, 2000); // 2000ms delay

		return () => clearTimeout(timeoutId); // Cleanup timeout on searchTerm change
	}, [searchTerm, trackingThemeName]);

	const IconsList = mainArea.items;

	// Group policies by `policy.group.title`
	const groupedPolicies = policies?.reduce((policyGroups: Record<string, Policy[]>, policy) => {
		const groupTitle = policy.group?.title || 'Andet';

		if (!policyGroups[groupTitle]) {
			policyGroups[groupTitle] = [];
		}
		policyGroups[groupTitle].push(policy);
		return policyGroups;
	}, {});

	// Filter grouped policies based on the search term
	const filteredGroupedPolicies = Object.entries(groupedPolicies || {}).reduce(
		(filteredPolicyGroups: Record<string, Policy[]>, [groupTitle, groupPolicies]) => {
			const groupMatches = groupTitle.toLowerCase().includes(searchTerm.toLowerCase());

			const filteredPolicies = groupPolicies.filter(
				(policy) =>
					policy.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					policy.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					policy.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					policy.policy_number?.includes(searchTerm)
			);

			if (groupMatches || filteredPolicies.length > 0) {
				filteredPolicyGroups[groupTitle] = filteredPolicies;
			}

			return filteredPolicyGroups;
		},
		{}
	);

	const handlePolicyAction = (policy: Policy) => {
		const downloadTracking = DownloadTracking(
			'policy',
			'pdf',
			isMobile || isTablet ? 'download' : 'preview',
			trackingThemeName
		);

		handlePolicyClick(policy.policy_number, policy.title, downloadTracking);
	};

	const handleExportObjects = async (policy: Policy) => {
		if (!policy.policy_number) return;
		try {
			const payload = await dispatch(fetchPolicyObjectsExport({ policyNo: policy.policy_number })).unwrap();
			downloadBlobFile({ data: payload.data, fileName: payload.fileName });
		} catch (e) {}
	};

	// Preview (or download on mobile) policy overview handler using generic blob preview
	const handleDownloadOverview = async () => {
		try {
			const { dataUrl, fileName } = await dispatch(fetchPolicyOverviewDocument()).unwrap();
			const blob = dataUrlToBlob(dataUrl); // Removed fetch()
			await openBlobPreview({
				blob,
				name: fileName,
				contentType: 'application/pdf',
				displayAsPdf: true,
				downloadTrackingData: DownloadTracking(
					'policy-overview',
					'pdf',
					isMobile || isTablet ? 'download' : 'preview',
					trackingThemeName
				),
			});
		} catch (e) {}
	};

	const getGroupIcon = (groupTitle: string) => {
		const groupType = groupedPolicies[groupTitle]?.[0]?.group?.type || '';
		return IconsList.find((item) => item.content.title === groupType)?.content?.icon?.url || '';
	};

	const shouldShowStatusMessage =
		status !== HttpStatusCode.Loading &&
		(status === HttpStatusCode.InternalServerError ||
			status === HttpStatusCode.NoContent ||
			Object.keys(filteredGroupedPolicies)?.length === 0);

	return (
		<>
			<Grid
				className='pb-spacing-r'
				container
			>
				{/* Left Section: Headline */}
				<GridCell
					mobileWidth='12col'
					tabletWidth='6col'
					desktopWidth='6col'
					nogutter
					className={styles.headlineContainer}
				>
					<Headline
						title={title}
						size='sm'
						variant='h1'
						fontColor='primary'
					/>

					<Paragraph
						text={`Her kan du se et samlet overblik over dine forsikringer.`}
						fontColor='primaryDark'
						paragraphType={{
							type: 'standfirst',
							kind: 'title',
						}}
						className={styles.headlineContainer__subHeadline}
					/>
				</GridCell>

				{/* Right Section: Search Input */}
				<GridCell
					mobileWidth='12col'
					tabletWidth='6col'
					desktopWidth='6col'
					nogutter
					className={styles.searchContainer}
				>
					<SearchField
						placeholder={'Søg i alle forsikringer'}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</GridCell>
			</Grid>

			{/* Download overview button in its own grid below the headline/search grid */}
			{!shouldShowStatusMessage && (
				<Grid
					className={styles.PolicyList__overview}
					container
					verticalAligment='center'
				>
					<GridCell
						mobileWidth='12col'
						tabletWidth='6col'
						desktopWidth='6col'
						nogutter
					>
						<Button
							buttonType='tertiary'
							size='small'
							onClick={handleDownloadOverview}
							className={styles.PolicyList__overviewDownloadBtn}
						>
							Download overblik
							<span style={{ display: 'inline-flex', marginLeft: 0, verticalAlign: 'middle' }}>
								<SystemIcons
									icon='download'
									themeName={designSystemThemeName}
									width={24}
									height={24}
								/>
							</span>
						</Button>
					</GridCell>

					<GridCell
						mobileWidth='12col'
						tabletWidth='6col'
						desktopWidth='6col'
						nogutter
						textAlign='right'
					>
						<Paragraph
							paragraphType={{
								kind: 'paragraph',
								size: 'sm',
							}}
							text={`Samlet pris i alt`}
							fontColor='secondaryDarkGrey'
						/>

						<Paragraph
							paragraphType={{
								kind: 'paragraph',
								size: 'default',
							}}
							fontColor='primary'
							text={`${total?.amount?.toLocaleString('da-DK') || '0'} kr./ år`}
						/>
					</GridCell>
				</Grid>
			)}

			{status === HttpStatusCode.Loading && <LoadingSpinner />}

			{shouldShowStatusMessage && (
				<RichText
					className={classNames(styles.PolicyList__statusMessage, 'RichText')}
					description={getStatusMessage(
						statusMessages,
						status == HttpStatusCode.OK && Object.keys(filteredGroupedPolicies)?.length < 1
							? HttpStatusCode.NoContent
							: status
					)}
				/>
			)}

			{!shouldShowStatusMessage &&
				Object.entries(filteredGroupedPolicies)
					.sort(([a], [b]) => a.localeCompare(b, 'da'))
					.map(([groupTitle, groupPolicies]) => (
						<div
							key={groupTitle}
							className={styles.PolicyGroup}
						>
							<div className={styles.PolicyGroup__header}>
								<img
									src={getGroupIcon(groupTitle)}
									width={56}
									height={56}
								/>

								<Paragraph
									text={groupTitle}
									fontColor='primaryDark'
									paragraphType={{
										type: 'title',
										kind: 'title',
									}}
								/>
								{groupTitle === 'Sundhed' && (
									<span className={styles.PolicyList__groupTag}>
										Medarbejderlister kan downloades
									</span>
								)}
							</div>

							<div className={styles.PolicyList}>
								{groupPolicies.map((policy, index) => (
									<MultiColItem
										key={index}
										items={[
											{
												text: policy.title,
												color: 'primaryDark',
												className: styles.PolicyList__truncateCell,
												subText: { color: 'secondaryDarkGrey', text: policy.subtitle || '' },
											},
											{
												text: `${policy.price?.amount?.toLocaleString('da-DK') || '0'} ${policy.price?.unit || 'kr'}. /år | Forfald: ${formatDate(policy.end?.date) || ''}`,
												color: 'primaryDark',
												afterPipeColor: 'secondaryDarkGrey', // color the part after "|"
											},
										]}
										onClick={() => handlePolicyAction(policy)}
										action={
											<div className={styles.PolicyList__actionContainer}>
												{policy.title === 'Sundhedsforsikring' && (
													<ExportCsvButton
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															handleExportObjects(policy);
														}}
														title='Eksporter CSV'
													/>
												)}
												<PolicyPreviewButton policyNumber={`Police: ${policy.policy_number}`} />
											</div>
										}
									/>
								))}
							</div>
						</div>
					))}

			{openModal && (
				<PreviewModal
					displayAsPdf={previewDocument.displayAsPdf}
					data={previewDocument.data}
					handleMessageDownload={handlePolicyDownload}
					onClose={onModalClose}
				/>
			)}

			{error && (
				<Modal
					handleClose={() => resetError()}
					mode={'feedback'}
					mobileVariation={'dialog'}
				>
					<RichText description={getStatusMessage(statusMessages, HttpStatusCode.NotFound)} />
				</Modal>
			)}
		</>
	);
};

export default PolicyListComponent;
